// apps/web/src/components/AuthPanel.tsx
import * as React from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useLocale } from '../context/LocaleContext';
import { getAuthRedirectUrl, isNativeApp, openExternalUrl } from '../lib/appPlatform';

// Supabase's own minimum. Worth stating up front rather than letting people
// discover it by having a sign-up rejected.
const MIN_PASSWORD_LENGTH = 6;

/**
 * Supabase speaks English and speaks in API terms. The handful of failures a
 * real user actually runs into deserve their own wording; everything else falls
 * back to the original message, which beats a vague catch-all.
 */
function friendlyAuthError(
  error: { code?: string; message: string },
  t: (key: string, fallback?: string) => string
) {
  const code = (error as any)?.code || '';
  if (code === 'weak_password' || /at least \d+ characters/i.test(error.message)) {
    return t('Password must be at least 6 characters.', 'Password must be at least 6 characters.');
  }
  if (code === 'over_email_send_rate_limit' || code === 'over_request_rate_limit') {
    return t(
      'Too many attempts. Please wait a minute and try again.',
      'Too many attempts. Please wait a minute and try again.'
    );
  }
  if (code === 'user_already_exists' || code === 'email_exists') {
    return t('This email is already registered. Sign in instead, or use "Forgotten password?".');
  }
  if (code === 'email_address_invalid' || code === 'validation_failed') {
    return t('Please enter a valid email address.', 'Please enter a valid email address.');
  }
  return error.message;
}

export default function AuthPanel({
  // Lets a host (the auth modal in App.tsx) keep its own header in step with the
  // mode, which the panel owns. Optional — the standalone /login page passes none.
  onModeChange,
  // The modal draws its own titled header, so the panel drops its title and card
  // there instead of nesting a second one inside it.
  variant = 'page',
}: {
  onModeChange?: (mode: 'signin' | 'signup') => void;
  variant?: 'page' | 'modal';
}) {
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  // Which provider is mid-handshake, so the spinner lands on the button the user
  // actually pressed.
  const [oauthBusy, setOauthBusy] = React.useState<'google' | 'apple' | null>(null);
  const [showPassword, setShowPassword] = React.useState(false);
  const [msg, setMsg] = React.useState<string | null>(null);
  const [msgError, setMsgError] = React.useState(false);
  const [mode, setMode] = React.useState<'signin' | 'signup'>('signin');
  // Shown once we know a verification mail is genuinely outstanding, so a user
  // whose mail never arrived is not stuck with no way to ask for another.
  const [canResend, setCanResend] = React.useState(false);
  const { t } = useLocale();

  const redirectTo = getAuthRedirectUrl();
  // Autofill and mobile keyboards love to hand back " Me@Example.com ", which
  // Supabase then fails to match against the stored address.
  const cleanEmail = email.trim().toLowerCase();

  React.useEffect(() => {
    onModeChange?.(mode);
  }, [mode, onModeChange]);

  // Switching mode clears anything carried over from the other one — a stale
  // error, or a confirmation that no longer has a field to match against.
  function switchMode(next: 'signin' | 'signup') {
    setMode(next);
    setMsg(null);
    setMsgError(false);
    setCanResend(false);
    setConfirmPassword('');
  }

  async function signUp() {
    // Caught before the network call, so a typo costs nothing.
    if (password !== confirmPassword) {
      setMsgError(true);
      setMsg(t('Passwords do not match.'));
      return;
    }
    setBusy(true);
    setMsg(null);
    setMsgError(false);
    setCanResend(false);
    try {
      const { data, error } = await supabase.auth.signUp({
        email: cleanEmail,
        password,
        options: { emailRedirectTo: redirectTo },
      });
      if (error) {
        setMsgError(true);
        setMsg(friendlyAuthError(error, t));
        return;
      }
      // signUp resolves without an error in three quite different situations, and
      // only one of them actually sends a mail — so `data` has to be inspected.
      if (data.session) {
        // Email confirmation is disabled on the project: the account is live and
        // we are already signed in. Telling this user to check their inbox would
        // send them off waiting for a mail that is never sent.
        setMsgError(false);
        setMsg(null);
        window.dispatchEvent(new Event('auth:changed'));
        return;
      }
      if (data.user && data.user.identities?.length === 0) {
        // The address is already registered. Supabase fakes a success here on
        // purpose, so signup cannot be used to enumerate accounts, and sends no
        // mail — an empty `identities` array is the documented way to spot it.
        setMsgError(true);
        setMsg(t('This email is already registered. Sign in instead, or use "Forgotten password?".'));
        setMode('signin');
        return;
      }
      setMsgError(false);
      setMsg(t('Check your email to verify your account.'));
      setCanResend(true);
    } catch (err: any) {
      setMsgError(true);
      setMsg(err?.message || t('Sign-up failed', 'Sign-up failed'));
    } finally {
      setBusy(false);
    }
  }

  async function signIn() {
    setBusy(true);
    setMsg(null);
    setMsgError(false);
    setCanResend(false);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: cleanEmail,
        password,
      });
      if (error) {
        setMsgError(true);
        // An unconfirmed account is not a wrong password — saying "invalid
        // credentials" sends the user off resetting a password that works fine.
        if ((error as any)?.code === 'email_not_confirmed' || /not confirmed/i.test(error.message)) {
          setMsg(t('Your email is not verified yet. Check your inbox, or resend the link below.'));
          setCanResend(true);
        } else {
          // Normalize error to a friendly message
          setMsg(t('Invalid login credentials'));
        }
      } else {
        // Notify the shell (Header) to close the auth modal and update UI
        setMsgError(false);
        window.dispatchEvent(new Event('auth:changed'));
      }
    } catch (err: any) {
      setMsgError(true);
      setMsg(err?.message || t('Invalid login credentials'));
    } finally {
      setBusy(false);
    }
  }

  async function handleResendVerification() {
    if (!cleanEmail) {
      setMsgError(true);
      setMsg(t('Please enter your email first.'));
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: cleanEmail,
        options: { emailRedirectTo: redirectTo },
      });
      if (error) {
        setMsgError(true);
        setMsg(friendlyAuthError(error, t));
      } else {
        setMsgError(false);
        setMsg(t('Verification email sent again. It can take a few minutes — check your spam folder too.'));
      }
    } finally {
      setBusy(false);
    }
  }

  async function signInWithOAuthProvider(provider: 'google' | 'apple') {
    setBusy(true);
    setOauthBusy(provider);
    setMsg(null);
    setMsgError(false);
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo,
          ...(isNativeApp() ? { skipBrowserRedirect: true } : {}),
          // Force Google to always show the account chooser instead of silently
          // reusing its existing SSO session, so users with multiple Google
          // accounts can switch between them after logging out.
          ...(provider === 'google' ? { queryParams: { prompt: 'select_account' } } : {}),
        },
      });
      if (error) throw error;
      if (isNativeApp()) {
        if (!data?.url) throw new Error('No OAuth URL returned');
        await openExternalUrl(data.url);
      }
    } catch (error: any) {
      setMsgError(true);
      setMsg(error?.message || t('Sign-in failed', 'Sign-in failed'));
    } finally {
      setBusy(false);
      setOauthBusy(null);
    }
  }

  async function handleForgotPassword() {
    if (!cleanEmail) {
      setMsgError(true);
      setMsg(t('Please enter your email first.'));
      return;
    }
    setBusy(true);
    setMsg(null);
    setMsgError(false);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(cleanEmail, {
        redirectTo,
      });
      if (error) {
        setMsgError(true);
        setMsg(friendlyAuthError(error, t));
      } else {
        setMsgError(false);
        setMsg(t('If an account exists for this email, a reset link has been sent.'));
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (mode === 'signin') {
      await signIn();
    } else {
      await signUp();
    }
  }

  const title =
    mode === 'signin' ? t('Sign in to Your Account') : t('Create Your Account');

  return (
    <div
      className={
        variant === 'modal'
          ? 'space-y-4'
          : 'rounded-2xl border p-6 space-y-4 max-w-md'
      }
    >
      {/* Title — the modal host already shows one of its own */}
      {variant === 'page' && (
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">{title}</h2>
        </div>
      )}

      <div className="grid gap-2 sm:grid-cols-2">
        <button
          type="button"
          className="btn w-full justify-center flex items-center gap-2"
          onClick={() => signInWithOAuthProvider('apple')}
          disabled={busy}
        >
          {oauthBusy === 'apple' ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <svg
              aria-hidden="true"
              viewBox="0 0 384 512"
              className="w-4 h-4"
              fill="currentColor"
            >
              <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
            </svg>
          )}
          <span>
            {mode === 'signup'
              ? t('Sign up with Apple', 'Sign up with Apple')
              : t('Sign in with Apple', 'Sign in with Apple')}
          </span>
        </button>
        <button
          type="button"
          className="btn w-full justify-center flex items-center gap-2"
          onClick={() => signInWithOAuthProvider('google')}
          disabled={busy}
        >
          {oauthBusy === 'google' ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <img
              src="/google-icon.svg"
              alt=""
              aria-hidden="true"
              className="w-4 h-4"
            />
          )}
          <span>
            {mode === 'signup'
              ? t('Sign up with Google', 'Sign up with Google')
              : t('Sign in with Google')}
          </span>
        </button>
      </div>

      {/* Divider */}
      <div className="flex items-center my-2">
        <div className="flex-1 h-px bg-gray-200 dark:bg-gray-800" />
        <span className="px-3 text-xs uppercase tracking-wide text-gray-400">
          {t('or')}
        </span>
        <div className="flex-1 h-px bg-gray-200 dark:bg-gray-800" />
      </div>

      {/* Message (errors / info) */}
      {msg && (
        <div className={`text-sm mb-1 ${msgError ? 'text-red-600' : 'opacity-80'}`}>
          {msg}
        </div>
      )}

      {/* Resend verification — only once we know a mail is actually outstanding */}
      {canResend && (
        <button
          type="button"
          className="text-xs text-blue-600 hover:underline disabled:opacity-50"
          onClick={handleResendVerification}
          disabled={busy}
        >
          {t('Resend verification email')}
        </button>
      )}

      {/* Email/password form */}
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-sm font-medium mb-1">
            {t('Email Address')}
          </label>
          <input
            className="input w-full"
            placeholder="you@example.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            autoComplete="email"
            type="email"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            {t('Password')}
          </label>
          <div className="relative">
            <input
              className="input w-full pr-10"
              placeholder="********"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
              minLength={mode === 'signup' ? MIN_PASSWORD_LENGTH : undefined}
              required
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 px-3 flex items-center opacity-60 hover:opacity-100"
              onClick={() => setShowPassword(v => !v)}
              aria-label={showPassword ? t('Hide password', 'Hide password') : t('Show password', 'Show password')}
              title={showPassword ? t('Hide password', 'Hide password') : t('Show password', 'Show password')}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {mode === 'signup' && (
            <div className="mt-1 text-xs opacity-60">
              {t('At least 6 characters.', 'At least 6 characters.')}
            </div>
          )}
        </div>

        {/* Confirm password (sign-up only) */}
        {mode === 'signup' && (
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('Confirm Password')}
            </label>
            <input
              className="input w-full"
              placeholder="********"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
            {confirmPassword !== '' && password !== confirmPassword && (
              <div className="mt-1 text-xs text-red-600">{t('Passwords do not match.')}</div>
            )}
          </div>
        )}

        {/* Forgotten password (sign-in only) */}
        {mode === 'signin' && (
          <div className="text-right">
            <button
              type="button"
              className="text-xs text-blue-600 hover:underline"
              onClick={handleForgotPassword}
              disabled={busy}
            >
              {t('Forgotten password?')}
            </button>
          </div>
        )}

        {/* Primary submit button */}
        <button
          type="submit"
          className="btn w-full justify-center flex items-center gap-2"
          disabled={busy}
        >
          {busy && !oauthBusy && <Loader2 size={16} className="animate-spin" />}
          <span>
            {busy && !oauthBusy
              ? mode === 'signin'
                ? t('Signing in…', 'Signing in…')
                : t('Creating account…', 'Creating account…')
              : mode === 'signin'
              ? t('Sign In')
              : t('Sign Up')}
          </span>
        </button>

        {/* Consent — the modal covers the footer where these normally live */}
        {mode === 'signup' && (
          <p className="text-xs opacity-60 text-center">
            {t('By creating an account you agree to our', 'By creating an account you agree to our')}{' '}
            <Link className="underline hover:opacity-100" to="/terms">
              {t('Terms', 'Terms')}
            </Link>{' '}
            {t('and', 'and')}{' '}
            <Link className="underline hover:opacity-100" to="/privacy">
              {t('Privacy Policy', 'Privacy Policy')}
            </Link>
            .
          </p>
        )}
      </form>

      {/* Bottom toggle: Need an Account? / Already have an account? */}
      <div className="mt-2 text-center text-sm">
        {mode === 'signin' ? (
          <>
            {t('Need an Account?')}{' '}
            <button
              type="button"
              className="text-blue-600 hover:underline font-medium"
              onClick={() => switchMode('signup')}
              disabled={busy}
            >
              {t('Sign up')}
            </button>
          </>
        ) : (
          <>
            {t('Already have an Account?')}{' '}
            <button
              type="button"
              className="text-blue-600 hover:underline font-medium"
              onClick={() => switchMode('signin')}
              disabled={busy}
            >
              {t('Sign in')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
